import { useEffect, useState } from 'react';
import { config } from '../config';
import { stateByCode, type UsState } from '../content/states';

export type ProjectType = 'PRIVATE' | 'PUBLIC' | 'FEDERAL';
/** One notice PayRight can prepare in a jurisdiction (`apps/api/src/rules/public-coverage.ts`). */
export type CoverageNotice = { kind: string; title: string | null; citation: { label: string; url: string } | null;
  projectTypes: ProjectType[]; directoryFiling: boolean; notarized: boolean; attorneyApproved: boolean };
type CoverageResponse = { jurisdictions: Array<{ jurisdiction: string; notices: CoverageNotice[]; noNoticeProjectTypes?: ProjectType[] }> };

/**
 * Where PayRight has a live rule: each state's notices, and the federal (Miller Act) ones that apply in every state; and
 * each state's kinds of job where a live rule finds no preliminary notice is needed. `states` holds only states with a notice.
 */
export type Coverage = { states: Map<string, CoverageNotice[]>; federal: CoverageNotice[]; noNotice: Map<string, ProjectType[]> };

export const coverageUrl = (graphqlUrl = config.graphqlUrl) => graphqlUrl.replace(/\/graphql\/?$/, '/notice-coverage');

export function readCoverage(body: CoverageResponse): Coverage {
  const states = new Map<string, CoverageNotice[]>();
  const noNotice = new Map<string, ProjectType[]>();
  let federal: CoverageNotice[] = [];
  for (const { jurisdiction, notices, noNoticeProjectTypes = [] } of body.jurisdictions) {
    if (jurisdiction === 'US') federal = notices;
    else if (stateByCode(jurisdiction.slice(3))) {
      if (notices.length) states.set(jurisdiction.slice(3), notices);
      if (noNoticeProjectTypes.length) noNotice.set(jurisdiction.slice(3), noNoticeProjectTypes);
    }
  }
  return { states, federal, noNotice };
}

let pending: Promise<Coverage> | null = null;
function loadCoverage() {
  pending ??= fetch(coverageUrl()).then(async response => {
    if (!response.ok) throw new Error(`Coverage request failed (${response.status})`);
    return readCoverage(await response.json() as CoverageResponse);
  }).catch(error => { pending = null; throw error; });
  return pending;
}

export type CoverageState = { status: 'loading' } | { status: 'ready'; coverage: Coverage } | { status: 'failed' };

/** Coverage, fetched once per page load and shared by every page that shows it. */
export function useCoverage(): CoverageState {
  const [state, setState] = useState<CoverageState>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    loadCoverage().then(coverage => { if (live) setState({ status: 'ready', coverage }); }, () => { if (live) setState({ status: 'failed' }); });
    return () => { live = false; };
  }, []);
  return state;
}

const byName = (codes: Iterable<string>) => [...codes].map(code => stateByCode(code)!).sort((a, b) => a.name.localeCompare(b.name));
/** The states with a live notice rule, in alphabetical order (DC counts as one). */
export const coveredStates = (coverage: Coverage): UsState[] => byName(coverage.states.keys());
/** The states whose live rules find no preliminary notice needed on any kind of job they cover, and prepare none. */
export const noNoticeStates = (coverage: Coverage): UsState[] => byName([...coverage.noNotice.keys()].filter(code => !coverage.states.has(code)));

/** "45 states and DC", said the way a person would. */
export function coverageCount(coverage: Coverage) {
  const covered = coveredStates(coverage);
  const dc = covered.some(state => state.code === 'DC');
  const states = covered.length - (dc ? 1 : 0);
  return `${states} ${states === 1 ? 'state' : 'states'}${dc ? ' and DC' : ''}`;
}

/** For tests: forget the shared fetch. */
export const resetCoverageForTests = () => { pending = null; };
